import type { TestRunResult } from '../engine/testRuntime';

type SuiteResult = TestRunResult['suites'][number];

/**
 * `@gotti/sandbox-core`'s `compileAndLoad`, as the runners call it. Declared here rather than imported: sandbox-core
 * depends on sandbox-test, so sandbox-test never imports it statically, its types included — each runner resolves the
 * real one (lazily) and hands it in.
 */
export interface CompileAndLoad<Options = unknown> {
  (
    entryFile: string,
    virtualFiles: Map<string, string>,
    moduleCache: Map<string, unknown>,
    externalModules: Map<string, unknown>,
    sandboxConsole?: Console,
    options?: Options,
  ): unknown;
}

/**
 * Loads one spec file through `load` (sandbox-core's `compileAndLoad`, which each runner imports as it must — the node
 * runner lazily) — registering its tests — and returns why it did not load, if it did not: the first module failure
 * logged while it loaded, or a throw. `compileAndLoad` catches a module's failure — transpile or evaluation, a spec
 * whose top level throws included — logs `[Sandbox] Failed to compile <path>:` and goes on, so a runner that only
 * caught a throw saw nothing, and the spec's tests silently vanished from its count. Every runner (node, browser, the
 * site's) loads its specs through this, so a spec that does not load is the same `load error` in each.
 */
export function loadSpecFile<Options>(
  load: CompileAndLoad<Options>,
  path: string,
  virtualFiles: Map<string, string>,
  moduleCache: Map<string, unknown>,
  externalModules: Map<string, unknown>,
  sandboxConsole: Console = console,
  options?: Options,
): { loadError?: unknown } {
  let failed = false;
  let loadError: unknown;
  const capturing = new Proxy(sandboxConsole, {
    get(target, property, receiver) {
      if (property !== 'error') return Reflect.get(target, property, receiver);
      return (...args: unknown[]) => {
        if (!failed && typeof args[0] === 'string' && args[0].startsWith('[Sandbox] Failed to compile')) {
          failed = true;
          loadError = args[1];
        }
        return target.error(...args);
      };
    },
  });
  try {
    load(path, virtualFiles, moduleCache, externalModules, capturing, options);
  } catch (err) {
    if (!failed) {
      failed = true;
      loadError = err;
    }
  }
  return failed ? { loadError } : {};
}

/** A spec that did not load, as every runner reports it: one failing `load error` test on a suite named for the file. */
export function specLoadErrorSuite(path: string, loadError: unknown): SuiteResult {
  const message = loadError instanceof Error ? loadError.message : String(loadError);
  return {
    name: path,
    file: path,
    tests: [{ name: 'load error', suiteName: path, file: path, status: 'error', error: { name: 'LoadError', message } }],
    passed: 0,
    failed: 1,
    skipped: 0,
  };
}
